import React, { useState, useCallback, useEffect } from "react";
import CommonFormLabel from "../../components/formLabel";
import Input from "../../components/input";
import ValidationFeedback from "../../components/validationFeedback";
import CommonButton from "../../components/commonButton";
import SimpleDropdown from "../../components/simpleDropdown";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";
import { Card, Toast, Form, Row, Col } from "react-bootstrap";
import { Formik } from "formik";
import { updateState, getStateDetails } from "./stateSlice";
import { stateEditSchema } from "../../schema/validationSchemas";
import { getCountries } from "../Settings/settingSlice";

export default function Edit() {
  const { id: stateId } = useParams();
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const dispatch = useDispatch();
  const [state, setState] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countriesOptions, setCountriesOptions] = useState([]);

  const fetchCountries = useCallback(async () => {
    try {
      await dispatch(getCountries({})).then((res) => {
        if (res.payload) {
          if (res.payload.length > 0) {
            const formattedData = res.payload.map((country) => ({
              value: country.id,
              label: country.name,
            }));
            setCountriesOptions(formattedData);
          }
        } else {
          setCountriesOptions([]);
        }
      });
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const fetchState = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dispatch(getStateDetails(stateId));
      setState(result.payload.data);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching state:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, stateId, setLoading]);

  useEffect(() => {
    setState(null);
    if (stateId) {
      fetchState();
    }
  }, [stateId, fetchState]);

  const handleCancel = () => {
    setState(null);
    navigate("/states");
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);
    const updatedValues = { ...values, id: stateId };

    const formData = new FormData();
    formData.append("id", updatedValues.id);
    formData.append("_method", "PUT");
    formData.append("name", updatedValues.name);
    formData.append("country_id", updatedValues.country_id);

    await dispatch(updateState(formData))
      .then((res) => {
        if (res.payload && res.payload.success) {
          localStorage.setItem("state_notify_message", res.payload.message);
          setSnackbarMessage(res.payload.message);
          setSnackbarVariant("success");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
          setState(null);
          navigate("/states");
        } else {
          setErrors(res.payload.data);
          setSnackbarMessage(res.payload.message || "Failed to update state!");
          setSnackbarVariant("danger");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        setSnackbarMessage("Failed to update state!");
        setSnackbarVariant("danger");
        setShowSnackbar(true);
        setSubmitting(false);
        setIsSubmitting(false);
      });
  };

  if (!state)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <h2>Loading State Details...</h2>
      </div>
    );

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>

      {state && (
        <Formik
          initialValues={{
            name: state.name || "",
            country_id: state.country_id || "",
          }}
          validationSchema={stateEditSchema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            handleChange,
            handleSubmit,
            values,
            setFieldValue,
          }) => (
            <Form
              onSubmit={handleSubmit}
              encType="multipart/form-data"
              className="bg-white shadow-lg rounded-lg p-4"
            >
              <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
                <h4 className="mb-4 d-flex justify-content-center">Basic</h4>
                <Row>
                  <Col md={6}>
                    <CommonFormLabel htmlFor="name" title="Name" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Enter Name"
                      onChange={handleChange}
                      value={values.name}
                      className={`shadow-none ${
                        errors?.name && touched.name ? "error" : "correct"
                      }`}
                    />
                    {touched.name && errors?.name && (
                      <ValidationFeedback title={errors?.name} />
                    )}
                  </Col>
                  <Col md={6}>
                    <SimpleDropdown
                      label="Country"
                      name="country_id"
                      value={values.country_id}
                      onChange={(selectedOption) => {
                        setFieldValue("country_id", selectedOption.value);
                      }}
                      options={countriesOptions}
                      placeholder="Select a Country"
                      error={errors?.country_id}
                      touched={touched.country_id}
                    />
                  </Col>
                </Row>
              </Card>

              <div className="d-flex align-items-center justify-content-end gap-3 common-popup-btn">
                <CommonButton
                  title={isSubmitting ? "Updating..." : "Update"}
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary primary-shadow"
                />
                <CommonButton
                  title="Cancel"
                  onClick={handleCancel}
                  className="btn-muted"
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
}
